/**
 * Iter 55 → 58.1 — ForgedOps Operations Console
 *
 * Audience: ForgedOps onboarding / support / operations staff.
 * NOT a customer-facing surface. Access gated to PLATFORM_ADMIN_EMAILS
 * (server-controlled). Tenant owners + tenant admins see "Not authorised."
 *
 * Capabilities (Iter 58.1 — clarified product model):
 *   - List every customer company + counts + owner + modules + status
 *   - Create new company (slug, branding, owner invite — full fields)
 *   - Disable / reactivate company
 *   - Reset company owner (invite a new owner)
 *   - Toggle modules per company
 *   - View tenant users (read-only support tool)
 *   - View tenant invitations + copy invite link for any pending invite
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PerfDiagnosticsPanel } from "@/components/PerfDiagnosticsPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Plus, Copy, ShieldOff, ShieldCheck, RotateCw, Settings2,
  Users, Mail, HardHat, Clock, Search, ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MarketingLeadsPanel from "@/components/admin/MarketingLeadsPanel";
import OnboardingCommandCenter from "@/components/admin/OnboardingCommandCenter";
import CustomerHealthPanel from "@/components/admin/CustomerHealthPanel";
import CustomerCommsPanel from "@/components/admin/CustomerCommsPanel";
import LifecycleAutomationPanel from "@/components/admin/LifecycleAutomationPanel";
import TrialCommandCenter from "@/components/admin/TrialCommandCenter";
import MyCustomersPanel from "@/components/admin/MyCustomersPanel";
import BillingOverridePanel from "@/components/admin/BillingOverridePanel";
import PlatformAlertsPanel from "@/components/admin/PlatformAlertsPanel";

const ALL_MODULES = [
  "projects", "plans", "pins", "workflows", "rfi", "photos",
  "objects", "utility_intelligence", "measurements", "documents", "reports",
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
};
const fmtRelative = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return null; }
};

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  // GH-14 (real-plan certification, 2026-09-30) — every 403 reason used
  // to collapse into the SAME "not authorised" dead end, regardless of
  // whether the account will NEVER have access (role_denied), just
  // hasn't enrolled MFA yet, or has a session that predates its own MFA
  // verification. The backend now sends a structured `code` precisely
  // for this.
  const [forbiddenReason, setForbiddenReason] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [modulesFor, setModulesFor] = useState(null);
  const [drawerFor, setDrawerFor] = useState(null);  // tenant for users/invites drawer
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/tenants");
      setTenants(data);
      setForbidden(false);
      setForbiddenReason(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
        setForbiddenReason(e?.response?.headers?.["x-fop-error-code"] || "role_denied");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load companies");
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (forbidden || !user?.is_platform_admin) {
    // GH-14 — three genuinely different situations, three different
    // (and correctly actionable) messages. Logging out and back in is
    // real, working guidance for the MFA-verification case (the login
    // flow already runs the MFA challenge); enrollment has no
    // self-service UI yet, so that's said plainly rather than inventing
    // a flow that doesn't exist.
    if (forbiddenReason === "mfa_verification_required") {
      return (
        <div className="p-8 max-w-md mx-auto mt-16 rounded-xl border border-[var(--fop-warn)] bg-[rgba(234,179,8,0.06)] text-center"
             data-testid="platform-admin-mfa-verification-required">
          <HardHat className="w-7 h-7 text-[var(--fop-warn)] mx-auto mb-3" />
          <div className="text-lg font-semibold text-[var(--fop-text)] mb-1">Re-verify to continue</div>
          <div className="text-sm text-[var(--fop-text-2)] mb-4">
            Your current session hasn't completed its authentication code
            check yet. This is NOT a permissions problem — log out and log
            back in to finish it, then you'll be able to access Operations.
          </div>
          <button
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="px-4 py-2 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-medium"
            data-testid="platform-admin-mfa-relogin"
            style={{ minHeight: 44 }}
          >
            Log out &amp; log back in
          </button>
        </div>
      );
    }
    if (forbiddenReason === "mfa_enrollment_required") {
      return (
        <div className="p-8 max-w-md mx-auto mt-16 rounded-xl border border-[var(--fop-warn)] bg-[rgba(234,179,8,0.06)] text-center"
             data-testid="platform-admin-mfa-enrollment-required">
          <HardHat className="w-7 h-7 text-[var(--fop-warn)] mx-auto mb-3" />
          <div className="text-lg font-semibold text-[var(--fop-text)] mb-1">Two-factor enrollment required</div>
          <div className="text-sm text-[var(--fop-text-2)]">
            Your account has Operations access, but two-factor authentication
            must be enrolled first before performing administrative actions.
            This is NOT a permissions denial — contact your platform admin to
            complete MFA enrollment for your account.
          </div>
        </div>
      );
    }
    return (
      <div className="p-8 max-w-md mx-auto mt-16 rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] text-center"
           data-testid="platform-admin-locked">
        <HardHat className="w-7 h-7 text-[var(--fop-text-3)] mx-auto mb-3" />
        <div className="text-lg font-semibold text-[var(--fop-text)] mb-1">Operations Console</div>
        <div className="text-sm text-[var(--fop-text-2)]">
          ForgedOps staff access only. Your account is not authorised to manage customer companies.
        </div>
      </div>
    );
  }

  const filtered = tenants.filter((t) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (t.display_name || "").toLowerCase().includes(q)
      || (t.slug || "").toLowerCase().includes(q)
      || (t.owner?.email || "").toLowerCase().includes(q)
      || (t.contact_email || "").toLowerCase().includes(q)
    );
  });
  const activeCount = tenants.filter((t) => t.status !== "disabled").length;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto" data-testid="platform-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.22em] text-[var(--fop-primary)] flex items-center gap-2 mb-1.5">
            <HardHat size={13} />
            ForgedOps Internal
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[var(--fop-text)]">Operations Console</h1>
          <div className="text-base text-[var(--fop-text-2)] mt-1.5">
            Customer companies, onboarding, support tools.
            <span className="ml-1.5 font-medium text-[var(--fop-text)]">
              {tenants.length} total · {activeCount} active
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/platform/users"
                data-testid="platform-users-link"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-md border text-sm font-medium hover:bg-[var(--fop-surface-2)]"
                style={{ borderColor: "var(--fop-border)" }}>
            <Users className="w-4 h-4" /> User Management
          </Link>
          <Button onClick={() => setCreateOpen(true)}
                   data-testid="platform-create-btn"
                   size="lg"
                   className="bg-[var(--fop-cta-bg)] hover:bg-[var(--fop-cta-bg-hover)] text-white">
            <Plus className="w-4 h-4 mr-2" /> Create company
          </Button>
        </div>
      </header>

      {/* TRACK 3 — Performance diagnostics. Local-only telemetry the
          operator can dump to confirm sheet open / zoom / pan targets. */}
      <PerfDiagnosticsPanel />

      {lastInvite && <LastInviteBanner inv={lastInvite} onClose={() => setLastInvite(null)} />}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, slug, or owner email…"
            className="w-full h-11 pl-10 pr-3 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm focus:border-[var(--fop-primary)] outline-none"
            data-testid="platform-filter"
          />
        </div>
        <div className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {filtered.length} shown
        </div>
      </div>

      <AlphaSignalsBlock />

      <PlatformAlertsPanel />

      <MyCustomersPanel />

      <MarketingLeadsPanel />

      <OnboardingCommandCenter />

      <TrialCommandCenter />

      <CustomerHealthPanel />

      <CustomerCommsPanel />

      <LifecycleAutomationPanel />

      <div className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[var(--fop-text-3)] text-sm">
            No companies match your filter.
          </div>
        ) : (
          <div className="divide-y divide-[var(--fop-border)]">
            {filtered.map((t) => (
              <TenantRow key={t.id} t={t}
                          onChanged={load}
                          onShowInvite={setLastInvite}
                          onConfigModules={() => setModulesFor(t)}
                          onOpenDrawer={() => setDrawerFor(t)} />
            ))}
          </div>
        )}
      </div>

      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)}
                            onCreated={(inv) => { setLastInvite(inv); load(); }} />

      {modulesFor && (
        <ModulesDialog tenant={modulesFor}
                         onClose={() => setModulesFor(null)}
                         onSaved={load} />
      )}

      {drawerFor && (
        <TenantSupportDrawer
          tenant={drawerFor}
          onClose={() => setDrawerFor(null)}
          onCopyInvite={setLastInvite}
        />
      )}
    </div>
  );
}

function TenantRow({ t, onChanged, onShowInvite, onConfigModules, onOpenDrawer }) {
  const disabled = t.status === "disabled";
  const owner = t.owner;
  const lastAct = fmtRelative(t.last_activity?.at);
  async function toggle() {
    const next = disabled ? "active" : "disabled";
    try {
      await api.patch(`/platform/tenants/${t.id}/status`,
                       { status: next,
                         reason: next === "disabled" ? "Disabled via Operations Console" : null });
      toast.success(next === "active" ? "Company reactivated" : "Company disabled");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  async function resetOwner() {
    const email = prompt("New owner email:");
    const name = email ? prompt("New owner name:") : null;
    if (!email || !name) return;
    try {
      const { data } = await api.post(`/platform/tenants/${t.id}/reset_owner`,
                                         { email, name });
      onShowInvite({ ...data.invitation, email, name });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }
  return (
    <div className="grid grid-cols-12 items-center gap-4 p-5 hover:bg-[var(--fop-surface-2)] transition-colors"
         data-testid={`tenant-row-${t.id}`}>
      <div className="col-span-12 md:col-span-4 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          {t.branding?.logo_url ? (
            <img src={t.branding.logo_url} alt="" className="w-7 h-7 rounded object-cover bg-[var(--fop-surface-2)]" />
          ) : (
            <div className="w-7 h-7 rounded grid place-items-center font-mono text-xs font-bold text-white"
                 style={{ background: t.branding?.primary_color || "var(--fop-primary)" }}>
              {(t.display_name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-base font-semibold text-[var(--fop-text)] truncate">{t.display_name}</div>
              {disabled && (
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[rgba(245,158,11,0.12)] text-[var(--fop-warning)] font-bold">
                  Disabled
                </span>
              )}
              {t.counts?.pending_invites > 0 && (
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] font-bold"
                      title={`${t.counts.pending_invites} pending invitation(s)`}>
                  {t.counts.pending_invites} pending
                </span>
              )}
              {t.feedback_counts?.total > 0 && (
                <button onClick={onOpenDrawer}
                         data-testid={`tenant-feedback-badge-${t.id}`}
                         title={`${t.feedback_counts.total} feedback items · ${t.feedback_counts.open} open`}
                         className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[var(--fop-surface-2)] hover:bg-[var(--fop-surface-3)] border border-[var(--fop-border)] text-[var(--fop-text-2)]">
                  {t.feedback_counts.P0 > 0 && <span className="text-red-300 font-bold">P0:{t.feedback_counts.P0}</span>}
                  {t.feedback_counts.P1 > 0 && <span className="text-amber-300 font-bold">P1:{t.feedback_counts.P1}</span>}
                  {t.feedback_counts.P2 > 0 && <span className="text-yellow-300 font-bold">P2:{t.feedback_counts.P2}</span>}
                  {t.feedback_counts.P3 > 0 && <span className="text-sky-300 font-bold">P3:{t.feedback_counts.P3}</span>}
                  {t.feedback_counts.open > 0 && (
                    <span className="text-[var(--fop-text-3)]">· {t.feedback_counts.open} open</span>
                  )}
                </button>
              )}
            </div>
            <div className="text-xs font-mono text-[var(--fop-text-3)] mt-0.5">{t.slug} · created {fmtDate(t.created_at)}</div>
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-3 min-w-0 text-sm">
        {owner ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono mb-0.5">Owner</div>
            <div className="text-[var(--fop-text)] truncate" data-testid={`tenant-owner-email-${t.id}`}>{owner.email}</div>
            <div className="text-xs text-[var(--fop-text-3)] truncate">
              {owner.name || "—"}
              {owner.status === "pending" && (
                <span className="ml-1.5 text-[var(--fop-warning)] font-mono uppercase tracking-wider text-[10px]">· invite pending</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-xs text-[var(--fop-text-3)] italic">No owner</div>
        )}
      </div>

      <div className="col-span-6 md:col-span-2 flex flex-col gap-1 text-sm text-[var(--fop-text-2)]">
        <div data-testid={`tenant-users-${t.id}`}>
          <span className="font-medium text-[var(--fop-text)]">{t.counts?.active_users}</span>
          /{t.counts?.users} users
        </div>
        <div data-testid={`tenant-projects-${t.id}`}>
          <span className="font-medium text-[var(--fop-text)]">{t.counts?.projects}</span> projects
          {t.counts?.sheets > 0 && (
            <span className="text-xs text-[var(--fop-text-3)] ml-1">· {t.counts.sheets} sheets</span>
          )}
        </div>
        <div className="text-xs">
          <span className="font-medium text-[var(--fop-text)]">{t.counts?.modules}</span> modules
        </div>
      </div>

      <div className="col-span-6 md:col-span-3 flex md:flex-col flex-wrap items-end md:items-end gap-1.5 justify-end text-xs">
        {lastAct && (
          <div className="flex items-center gap-1.5 text-[var(--fop-text-3)] font-mono">
            <Clock size={11} /> {lastAct}
          </div>
        )}
        <div className="flex gap-1.5 flex-wrap justify-end">
          <Button size="sm" variant="outline" asChild
                   data-testid={`tenant-open-command-center-${t.id}`} title="Open Tenant Command Center">
            <Link to={`/platform/tenants/${t.id}`}>
              <ChevronRight className="w-3.5 h-3.5 mr-1.5" /> Open
            </Link>
          </Button>
          <Button size="sm" variant="outline" onClick={onOpenDrawer}
                   data-testid={`tenant-support-${t.id}`} title="View users + invitations (support)">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Support
          </Button>
          <Button size="sm" variant="outline" onClick={onConfigModules}
                   data-testid={`tenant-modules-${t.id}`} title="Toggle modules">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Modules
          </Button>
          <Button size="sm" variant="outline" onClick={resetOwner}
                   data-testid={`tenant-reset-owner-${t.id}`} title="Invite a new owner">
            <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Reset owner
          </Button>
          <Button size="sm" variant="outline" onClick={toggle}
                   data-testid={`tenant-toggle-${t.id}`}
                   title={disabled ? "Reactivate company" : "Disable company"}>
            {disabled ? <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> : <ShieldOff className="w-3.5 h-3.5 mr-1.5" />}
            {disabled ? "Activate" : "Disable"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const empty = {
    company_name: "", company_slug: "",
    primary_color: "", secondary_color: "",
    logo_url: "", contact_email: "", contact_phone: "", website: "",
    initial_owner_email: "", initial_owner_name: "",
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ""));
      const { data } = await api.post("/platform/tenants", payload);
      toast.success(`Created ${data.tenant.display_name}`);
      onCreated({
        ...data.invitation,
        email: data.owner.email, name: data.owner.name,
      });
      onClose();
      setForm(empty);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create company");
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="create-tenant-dialog">
        <DialogHeader>
          <DialogTitle>Create new company</DialogTitle>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            Provisions tenant, branding, modules, and an owner invitation in one step.
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <SectionLabel>Company</SectionLabel>
          <Pair>
            <Field label="Company name" testid="create-name" required
                    value={form.company_name} onChange={(v) => set("company_name", v)}
                    placeholder="MASCI Construction" />
            <Field label="Slug (a-z, 0-9, -)" testid="create-slug" required
                    value={form.company_slug}
                    onChange={(v) => set("company_slug", v.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="masci" />
          </Pair>

          <SectionLabel>Initial owner</SectionLabel>
          <Pair>
            <Field label="Owner email" testid="create-owner-email" required type="email"
                    value={form.initial_owner_email} onChange={(v) => set("initial_owner_email", v)}
                    placeholder="owner@company.com" />
            <Field label="Owner name" testid="create-owner-name" required
                    value={form.initial_owner_name} onChange={(v) => set("initial_owner_name", v)}
                    placeholder="Marcus Halloway" />
          </Pair>

          <SectionLabel>Branding (optional)</SectionLabel>
          <Pair>
            <Field label="Primary color" testid="create-primary"
                    value={form.primary_color} onChange={(v) => set("primary_color", v)}
                    placeholder="#0d4d8c" />
            <Field label="Secondary color" testid="create-secondary"
                    value={form.secondary_color} onChange={(v) => set("secondary_color", v)}
                    placeholder="#0a1929" />
          </Pair>
          <Field label="Logo URL" testid="create-logo"
                  value={form.logo_url} onChange={(v) => set("logo_url", v)}
                  placeholder="https://cdn.example.com/logo.png" />

          <SectionLabel>Support contact (optional)</SectionLabel>
          <Pair>
            <Field label="Support email" testid="create-contact-email"
                    value={form.contact_email} onChange={(v) => set("contact_email", v)} type="email"
                    placeholder="support@company.com" />
            <Field label="Support phone" testid="create-contact-phone"
                    value={form.contact_phone} onChange={(v) => set("contact_phone", v)}
                    placeholder="+1-555-0100" />
          </Pair>
          <Field label="Website" testid="create-website"
                  value={form.website} onChange={(v) => set("website", v)}
                  placeholder="https://example.com" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}
                     className="bg-[var(--fop-cta-bg)] hover:bg-[var(--fop-cta-bg-hover)] text-white"
                     data-testid="create-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create company + invite owner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-primary)] pt-2 border-t border-[var(--fop-border)] mt-2 first:border-t-0 first:mt-0 first:pt-0">
      {children}
    </div>
  );
}

function Pair({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, value, onChange, testid, required, type = "text", placeholder }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)}
              required={required} type={type} placeholder={placeholder}
              data-testid={testid} className="mt-1" />
    </div>
  );
}

function LastInviteBanner({ inv, onClose }) {
  const fullUrl = `${window.location.origin}${inv.url}`;
  return (
    <section data-testid="platform-last-invite"
              className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 relative">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-emerald-400 mb-1">
        <Mail size={13} /> Invitation ready
      </div>
      <div className="text-sm text-[var(--fop-text)] mb-3">
        Send to <span className="font-mono">{inv.email}</span>
        {inv.name && <> — <span className="text-[var(--fop-text-2)]">{inv.name}</span></>}
        . Expires {new Date(inv.expires_at).toLocaleString()}.
      </div>
      <div className="flex gap-2 items-center">
        <code className="flex-1 truncate rounded bg-[var(--fop-surface-2)] px-3 py-2 text-xs"
              data-testid="platform-invite-link">{fullUrl}</code>
        <Button size="sm" variant="outline"
                 data-testid="platform-invite-copy"
                 onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success("Copied"); }}>
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
        </Button>
        <Button size="sm" variant="outline" onClick={onClose}>Dismiss</Button>
      </div>
    </section>
  );
}

function ModulesDialog({ tenant, onClose, onSaved }) {
  const [mods, setMods] = useState(tenant.modules_enabled || []);
  const [saving, setSaving] = useState(false);
  function toggle(m) {
    setMods((p) => p.includes(m) ? p.filter((x) => x !== m) : [...p, m]);
  }
  async function save() {
    setSaving(true);
    try {
      await api.patch(`/platform/tenants/${tenant.id}/modules`,
                       { modules_enabled: mods });
      toast.success("Modules updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="modules-dialog">
        <DialogHeader>
          <DialogTitle>{tenant.display_name} — modules</DialogTitle>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            Toggle the feature surfaces this company can access.
          </div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {ALL_MODULES.map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px] px-2 rounded hover:bg-[var(--fop-surface-2)]"
                    data-testid={`platform-module-${m}`}>
              <input type="checkbox" checked={mods.includes(m)}
                      onChange={() => toggle(m)} />
              <span>{m.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                   data-testid="platform-modules-save"
                   className="bg-[var(--fop-cta-bg)] hover:bg-[var(--fop-cta-bg-hover)] text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Iter 58.1 → 59.1 — Support drawer: read-only users + invitations + Feedback Center. */
function TenantSupportDrawer({ tenant, onClose, onCopyInvite }) {
  const [users, setUsers] = useState(null);
  const [invites, setInvites] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [feedbackCounts, setFeedbackCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("users");
  const [fbFilter, setFbFilter] = useState({ severity: "", status: "", role: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i, f] = await Promise.all([
        api.get(`/platform/tenants/${tenant.id}/users`),
        api.get(`/platform/tenants/${tenant.id}/invitations`),
        api.get(`/platform/tenants/${tenant.id}/feedback?limit=300`),
      ]);
      setUsers(u.data.items || u.data.users || []);
      setInvites(i.data.invitations || []);
      setFeedback(f.data.feedback || []);
      setFeedbackCounts(f.data.counts || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load support data");
    } finally { setLoading(false); }
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  const reloadFeedback = useCallback(async (overrides = null) => {
    const filt = overrides || fbFilter;
    const params = new URLSearchParams();
    if (filt.severity) params.set("severity", filt.severity);
    if (filt.status)   params.set("status", filt.status);
    if (filt.role)     params.set("role", filt.role);
    if (filt.q)        params.set("q", filt.q);
    params.set("limit", "300");
    const { data } = await api.get(`/platform/tenants/${tenant.id}/feedback?${params.toString()}`);
    setFeedback(data.feedback || []);
    setFeedbackCounts(data.counts || null);
  }, [tenant.id, fbFilter]);

  async function patchFeedback(id, body) {
    const { data } = await api.patch(`/platform/feedback/${id}`, body);
    return data.feedback;
  }

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="tenant-support-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-[var(--fop-primary)]" />
            {tenant.display_name} — Support
          </DialogTitle>
          <div className="text-sm text-[var(--fop-text-2)] mt-1">
            Read-only support tools. No impersonation. No customer document access.
          </div>
        </DialogHeader>

        {/* Founder cross-browser cert — this dialog's `overflow-y-auto` was
            implicitly computing `overflow-x: auto` too (CSS spec: an axis
            left at `visible` while the other is scrollable is promoted to
            `auto`). With 5 tabs wider than a mobile viewport, that made the
            WHOLE dialog horizontally scrollable and landed at a non-zero,
            visually-centered default scroll offset — clipping the start of
            every line (title, description, form fields) on phones. Fixed by
            (1) hiding overflow-x on the dialog itself (`overflow-x-hidden`
            above) and (2) scoping horizontal scroll to just this tab row,
            the standard mobile tab-overflow pattern used elsewhere. */}
        <div className="flex gap-1 mt-3 border-b border-[var(--fop-border)] overflow-x-auto">
          {[
            { id: "users",    label: `Users (${users?.length ?? "—"})` },
            { id: "invites",  label: `Invitations (${invites?.length ?? "—"})` },
            { id: "feedback", label: `Feedback (${feedbackCounts?.total ?? "—"})` },
            { id: "export",   label: "Export Company Data" },
            { id: "breakglass", label: "Break-Glass Access" },
            { id: "billing",  label: "Billing & Entitlements" },
          ].map((x) => (
            <button key={x.id} onClick={() => setTab(x.id)}
                    className={`min-h-[44px] px-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                      tab === x.id
                        ? "border-[var(--fop-primary)] text-[var(--fop-text)]"
                        : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                    }`}
                    data-testid={`support-tab-${x.id}`}>
              {x.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : tab === "users" ? (
          <UsersTable users={users} />
        ) : tab === "invites" ? (
          <InvitesTable invites={invites} onCopy={onCopyInvite} />
        ) : tab === "export" ? (
          <ExportTab tenant={tenant} />
        ) : tab === "breakglass" ? (
          <BreakGlassTab tenant={tenant} />
        ) : tab === "billing" ? (
          <BillingOverridePanel tenant={tenant} />
        ) : (
          <FeedbackTab
            tenant={tenant}
            feedback={feedback}
            counts={feedbackCounts}
            filter={fbFilter}
            setFilter={setFbFilter}
            onReload={reloadFeedback}
            onPatch={patchFeedback}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersTable({ users }) {
  if (!users || users.length === 0) {
    return <div className="p-6 text-center text-[var(--fop-text-3)] text-sm">No users yet.</div>;
  }
  return (
    <div className="divide-y divide-[var(--fop-border)]" data-testid="support-users-table">
      {users.map((u) => (
        <div key={u.id} className="grid grid-cols-12 items-center gap-2 py-2.5 text-sm" data-testid={`support-user-${u.id}`}>
          <div className="col-span-5 min-w-0">
            <div className="text-[var(--fop-text)] truncate">{u.name || u.email}</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono truncate">{u.email}</div>
          </div>
          <div className="col-span-2 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
            {u.role}
          </div>
          <div className="col-span-2 text-xs">
            {u.status === "disabled" || u.disabled ? (
              <span className="text-[var(--fop-warning)] font-mono uppercase tracking-wider">Disabled</span>
            ) : u.status === "pending" ? (
              <span className="text-[var(--fop-primary)] font-mono uppercase tracking-wider">Pending</span>
            ) : (
              <span className="text-emerald-400 font-mono uppercase tracking-wider">Active</span>
            )}
          </div>
          <div className="col-span-3 text-xs text-[var(--fop-text-3)] text-right">
            {u.last_login_at ? `signed in ${fmtRelative(u.last_login_at)}` : "never signed in"}
          </div>
        </div>
      ))}
    </div>
  );
}

function InvitesTable({ invites, onCopy }) {
  if (!invites || invites.length === 0) {
    return <div className="p-6 text-center text-[var(--fop-text-3)] text-sm">No invitations sent.</div>;
  }
  return (
    <div className="divide-y divide-[var(--fop-border)]" data-testid="support-invites-table">
      {invites.map((inv) => {
        const fullUrl = `${window.location.origin}${inv.url}`;
        return (
          <div key={inv.id} className="grid grid-cols-12 items-center gap-2 py-2.5 text-sm" data-testid={`support-invite-${inv.id}`}>
            <div className="col-span-5 min-w-0">
              <div className="text-[var(--fop-text)] truncate">{inv.name || inv.email}</div>
              <div className="text-xs text-[var(--fop-text-3)] font-mono truncate">{inv.email}</div>
            </div>
            <div className="col-span-2 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
              {inv.role}
            </div>
            <div className="col-span-2 text-xs">
              {inv.status === "pending" ? (
                <span className="text-[var(--fop-primary)] font-mono uppercase tracking-wider">Pending</span>
              ) : inv.status === "expired" ? (
                <span className="text-[var(--fop-warning)] font-mono uppercase tracking-wider">Expired</span>
              ) : (
                <span className="text-[var(--fop-text-3)] font-mono uppercase tracking-wider">Used</span>
              )}
            </div>
            <div className="col-span-3 flex items-center justify-end gap-1.5">
              {inv.status === "pending" && (
                <Button size="sm" variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(fullUrl);
                          toast.success("Invite link copied");
                          onCopy && onCopy({ ...inv, url: inv.url });
                        }}
                        data-testid={`support-invite-copy-${inv.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                </Button>
              )}
              {inv.status !== "pending" && (
                <span className="text-xs text-[var(--fop-text-3)] font-mono">
                  {fmtRelative(inv.used_at || inv.expires_at)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Iter 59.1 — Feedback tab inside the existing Support drawer. */
const FB_STATUSES = [
  { id: "new",             label: "New" },
  { id: "reviewed",        label: "Reviewed" },
  { id: "action_required", label: "Action Required" },
  { id: "planned",         label: "Planned" },
  { id: "closed",          label: "Closed" },
];
const SEVERITY_TONE = {
  P0: "bg-red-500/10 text-red-300 border-red-500/30",
  P1: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  P2: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  P3: "bg-sky-500/10 text-sky-300 border-sky-500/30",
};
const STATUS_TONE = {
  new:             "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] border-[var(--fop-primary)]/40",
  reviewed:        "bg-[var(--fop-surface-2)] text-[var(--fop-text-2)] border-[var(--fop-border)]",
  action_required: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  planned:         "bg-purple-500/12 text-purple-300 border-purple-500/40",
  closed:          "bg-emerald-500/12 text-emerald-300 border-emerald-500/40",
};

function FeedbackTab({ tenant, feedback, counts, filter, setFilter, onReload, onPatch }) {
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  function applyFilter(patch) {
    const next = { ...filter, ...patch };
    setFilter(next);
    onReload(next);
  }

  async function patch(id, body, optimistic) {
    try {
      const updated = await onPatch(id, body);
      toast.success(optimistic || "Updated");
      await onReload();
      return updated;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="mt-3" data-testid="feedback-tab">
      {/* Counters */}
      {counts && (
        <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="feedback-counters">
          {["P0", "P1", "P2", "P3"].map((c) => (
            <button key={c} type="button"
                     onClick={() => applyFilter({ severity: filter.severity === c ? "" : c })}
                     data-testid={`feedback-counter-${c}`}
                     className={`min-h-[36px] px-3 rounded-md border text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                       filter.severity === c
                         ? SEVERITY_TONE[c]
                         : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:border-[var(--fop-border-2)]"
                     }`}>
              <span className="font-bold">{c}</span>
              <span className="text-[var(--fop-text-3)]">{counts[c] || 0}</span>
            </button>
          ))}
          <span className="text-xs font-mono text-[var(--fop-text-3)] ml-2">
            {counts.total} total · {counts.open} open
          </span>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <select value={filter.status} onChange={(e) => applyFilter({ status: e.target.value })}
                 data-testid="feedback-filter-status"
                 className="h-10 px-2 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-sm text-[var(--fop-text)]">
          <option value="">All statuses</option>
          {FB_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={filter.role} onChange={(e) => applyFilter({ role: e.target.value })}
                 data-testid="feedback-filter-role"
                 className="h-10 px-2 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-sm text-[var(--fop-text)]">
          <option value="">All roles</option>
          {["owner", "admin", "project_manager", "superintendent", "foreman", "field_engineer", "viewer", "safety_manager"].map((r) =>
            <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </select>
        <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && onReload()}
                onBlur={() => onReload()}
                placeholder="Search message/page/sheet…"
                data-testid="feedback-filter-q"
                className="flex-1 min-w-[180px] h-10 px-3 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-sm text-[var(--fop-text)]" />
        {(filter.severity || filter.status || filter.role || filter.q) && (
          <button onClick={() => applyFilter({ severity: "", status: "", role: "", q: "" })}
                  className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)] underline">
            clear
          </button>
        )}
      </div>

      {/* List */}
      {!feedback || feedback.length === 0 ? (
        <div className="p-6 text-center text-[var(--fop-text-3)] text-sm">
          No feedback matches.
        </div>
      ) : (
        <div className="divide-y divide-[var(--fop-border)]" data-testid="feedback-list">
          {feedback.map((f) => {
            const expanded = expandedId === f.id;
            const draft = noteDraft[f.id] || "";
            return (
              <div key={f.id} className="py-3" data-testid={`feedback-row-${f.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`min-w-[34px] px-2 h-7 grid place-items-center rounded font-mono text-[11px] font-bold border ${SEVERITY_TONE[f.category]}`}>
                    {f.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="text-[var(--fop-text)] font-medium">{f.user_name || f.user_email}</span>
                      <span className="text-[var(--fop-text-3)]">·</span>
                      <span className="text-[var(--fop-text-2)] font-mono uppercase tracking-wider text-[10px]">{f.user_role || "—"}</span>
                      {f.project_label && (<>
                        <span className="text-[var(--fop-text-3)]">·</span>
                        <span className="text-[var(--fop-text-2)] truncate">{f.project_label}</span>
                      </>)}
                      {f.sheet_label && (<>
                        <span className="text-[var(--fop-text-3)]">·</span>
                        <span className="text-[var(--fop-text-2)] font-mono">{f.sheet_label}</span>
                      </>)}
                      <span className="text-[var(--fop-text-3)] ml-auto">{fmtRelative(f.created_at)}</span>
                    </div>
                    <div className="text-sm text-[var(--fop-text)] mt-1 leading-snug">
                      “{f.message}”
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[f.status] || STATUS_TONE.new}`}>
                        {FB_STATUSES.find((s) => s.id === f.status)?.label || f.status}
                      </span>
                      {f.page_path && (
                        <span className="text-[10px] font-mono text-[var(--fop-text-3)] truncate" title={f.page_path}>
                          {f.page_path}
                        </span>
                      )}
                      <button type="button" onClick={() => setExpandedId(expanded ? null : f.id)}
                              data-testid={`feedback-expand-${f.id}`}
                              className="ml-auto text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] underline">
                        {expanded ? "Hide" : `Triage${(f.internal_notes||[]).length ? ` · ${(f.internal_notes||[]).length} notes` : ""}`}
                      </button>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div className="mt-3 ml-12 pl-4 border-l-2 border-[var(--fop-primary)]/40 space-y-3">
                    {/* Status transitions */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono">Status:</span>
                      {FB_STATUSES.map((s) => (
                        <button key={s.id}
                                onClick={() => patch(f.id, { status: s.id }, `Marked ${s.label}`)}
                                disabled={f.status === s.id}
                                data-testid={`feedback-status-${f.id}-${s.id}`}
                                className={`min-h-[36px] px-3 rounded text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                                  f.status === s.id
                                    ? STATUS_TONE[s.id]
                                    : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:border-[var(--fop-border-2)] hover:text-[var(--fop-text)]"
                                }`}>
                          {s.label}
                        </button>
                      ))}
                    </div>

                    {/* Internal notes */}
                    {(f.internal_notes || []).length > 0 && (
                      <div className="space-y-1.5">
                        {f.internal_notes.map((n, idx) => (
                          <div key={idx} className="rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border)] p-2.5 text-sm">
                            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
                              {n.author_name || n.author_email} · {fmtRelative(n.at)} · ForgedOps-internal
                            </div>
                            <div className="text-[var(--fop-text)] whitespace-pre-wrap">{n.text}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add internal note */}
                    <div className="flex gap-2 items-start">
                      <textarea value={draft}
                                onChange={(e) => setNoteDraft({ ...noteDraft, [f.id]: e.target.value })}
                                rows={2}
                                placeholder="Internal note (ForgedOps-only — never shown to customer)…"
                                data-testid={`feedback-note-input-${f.id}`}
                                className="flex-1 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm p-2 focus:border-[var(--fop-primary)] outline-none resize-none" />
                      <Button size="sm"
                               disabled={!draft.trim()}
                               data-testid={`feedback-note-save-${f.id}`}
                               onClick={async () => {
                                 await patch(f.id, { internal_note: draft.trim() }, "Note added");
                                 setNoteDraft({ ...noteDraft, [f.id]: "" });
                               }}>
                        Save note
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Iter 59.1 — Alpha signal block. Inline. No dashboard. */
function AlphaSignalsBlock() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    api.get("/platform/feedback/_signals?since_days=30")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data || data.total_feedback === 0) return null;
  const Pill = ({ label, count, tone }) => (
    <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`}>
      {label} {count}
    </span>
  );
  return (
    <section className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-4" data-testid="alpha-signals">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-primary)] flex items-center gap-2">
          <HardHat size={12} /> Alpha Signals · last 30 days
        </div>
        <button onClick={() => setCollapsed((c) => !c)} className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>
      {collapsed ? null : (
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono mb-1.5">By severity · status</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {["P0","P1","P2","P3"].map((c) => (
                <Pill key={c} label={c} count={data.by_category[c] || 0}
                       tone={c==="P0"?"text-red-300 border-red-500/40":c==="P1"?"text-amber-300 border-amber-500/40":c==="P2"?"text-yellow-300 border-yellow-500/40":"text-sky-300 border-sky-500/40"} />
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Pill label="OPEN" count={data.open_feedback} tone="text-[var(--fop-primary)] border-[var(--fop-primary)]/40" />
              <Pill label="TOTAL" count={data.total_feedback} tone="text-[var(--fop-text-2)] border-[var(--fop-border)]" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono mb-1.5">Top complaint terms (P0/P1)</div>
            <div className="flex flex-wrap gap-1.5">
              {data.top_complaint_terms.slice(0, 8).map((t) => (
                <Pill key={t.value} label={t.value} count={t.count}
                       tone="text-amber-300 border-amber-500/30" />
              ))}
              {data.top_complaint_terms.length === 0 && (
                <span className="text-[var(--fop-text-3)] text-xs italic">no complaints yet</span>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono mt-3 mb-1.5">Top wishlist terms (P3)</div>
            <div className="flex flex-wrap gap-1.5">
              {data.top_wishlist_terms.slice(0, 8).map((t) => (
                <Pill key={t.value} label={t.value} count={t.count}
                       tone="text-sky-300 border-sky-500/30" />
              ))}
              {data.top_wishlist_terms.length === 0 && (
                <span className="text-[var(--fop-text-3)] text-xs italic">no wishes yet</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono mb-1.5">Most mentioned screens</div>
            <ul className="space-y-1 text-xs">
              {data.top_pages.slice(0, 6).map((p) => (
                <li key={p.value} className="flex justify-between gap-2 items-baseline">
                  <span className="text-[var(--fop-text-2)] font-mono truncate" title={p.value}>{p.value}</span>
                  <span className="text-[var(--fop-text-3)]">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}



/* Iter 61.1 — Export Company Data tab (platform-admin only). */
function ExportTab({ tenant }) {
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function requestExport() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/export`, {});
      setExports((xs) => [
        {
          id: data.export_id,
          status: data.status || "queued",
          created_at: new Date().toISOString(),
          cli_invocation: data.cli_invocation,
        },
        ...xs,
      ]);
      toast.success("Export queued — ForgedOps support will run the CLI to produce the bundle.");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="py-4 space-y-4" data-testid="tenant-export-tab">
      <header>
        <div className="text-sm font-medium text-[var(--fop-text)]">
          Export {tenant.display_name}
        </div>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 leading-5 max-w-prose">
          Produces a full snapshot of this tenant — every project, sheet, document,
          photo metadata row, user record (without password hashes), and audit
          trail entry. Platform-admin only. Each request is audited.
        </p>
      </header>

      <Button
        size="sm"
        onClick={requestExport}
        disabled={busy}
        data-testid="tenant-export-request-button"
      >
        {busy ? "Queueing…" : "Request Export"}
      </Button>

      {error ? (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2"
             data-testid="tenant-export-error">
          {error}
        </div>
      ) : null}

      <div className="border border-[var(--fop-border)] rounded">
        <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
          <span>Export ID</span>
          <span>Status</span>
          <span>Created</span>
          <span>Download</span>
        </div>
        {exports.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[var(--fop-text-3)]">
            No exports yet. Click <em>Request Export</em> above.
          </div>
        ) : (
          exports.map((x) => (
            <div key={x.id}
                 className="grid grid-cols-4 gap-2 px-3 py-2 text-xs items-center border-b border-[var(--fop-border)] last:border-b-0"
                 data-testid={`tenant-export-row-${x.id}`}>
              <span className="font-mono truncate" title={x.id}>{x.id}</span>
              <span>{x.status}</span>
              <span className="text-[var(--fop-text-3)]">
                {new Date(x.created_at).toLocaleString()}
              </span>
              <span className="text-[var(--fop-text-3)]">
                CLI: <code className="text-[10px]">{x.cli_invocation?.slice(0, 64)}…</code>
              </span>
            </div>
          ))
        )}
      </div>

      <details className="text-xs text-[var(--fop-text-3)]" data-testid="tenant-export-audit">
        <summary className="cursor-pointer text-[var(--fop-text-2)]">Audit trail</summary>
        <p className="mt-2 leading-5">
          Every export request emits a <code>platform.tenant_export_requested</code>{" "}
          audit row with the export id, slug, and requesting platform admin. The
          download artifact is produced by a ForgedOps operator running{" "}
          <code>backend/scripts/export_tenant.py</code> and lives at{" "}
          <code>tenants/{tenant.id}/_exports/&lt;export_id&gt;/</code> in R2.
        </p>
      </details>
    </section>
  );
}

function BreakGlassTab({ tenant }) {
  const [grants, setGrants] = useState(null);
  const [reason, setReason] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/break-glass", { params: { limit: 1000 } });
      setGrants((data.grants || []).filter((g) => g.tenant_id === tenant.id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load grants");
    }
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  const active = grants?.find((g) => g.status === "active");

  async function openGrant() {
    setBusy(true); setError(null);
    try {
      await api.post("/platform/break-glass", {
        tenant_id: tenant.id, reason, approved_by: approvedBy || undefined, duration_minutes: duration,
      });
      setReason(""); setApprovedBy("");
      toast.success("Break-glass grant opened — content access is now live and audited.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open grant");
    } finally { setBusy(false); }
  }

  async function revoke(id) {
    setBusy(true);
    try {
      await api.post(`/platform/break-glass/${id}/revoke`);
      setOverview(null);
      toast.success("Grant revoked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  }

  async function viewOverview(id) {
    try {
      const { data } = await api.get(`/platform/break-glass/${id}/content/overview`);
      setOverview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load content");
    }
  }

  return (
    <section className="py-4 space-y-4" data-testid="tenant-breakglass-tab">
      <header>
        <div className="text-sm font-medium text-[var(--fop-text)] flex items-center gap-1.5">
          <ShieldOff size={14} className="text-amber-400" /> Break-Glass Support Access
        </div>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 leading-5 max-w-prose">
          Zero routine access — ForgedOps staff cannot see this tenant's project content
          unless a scoped, time-boxed grant is open below. Every grant and every piece of
          content viewed under it is individually audit-logged and cannot be reused after
          it expires or is revoked.
        </p>
      </header>

      {error ? (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2"
             data-testid="breakglass-error">
          {error}
        </div>
      ) : null}

      {active ? (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 space-y-2" data-testid="breakglass-active-grant">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[var(--fop-text)] font-medium flex items-center gap-1.5">
              <Clock size={12} /> Active grant — expires {new Date(active.expires_at).toLocaleTimeString()}
            </div>
            <Button size="sm" variant="outline" onClick={() => revoke(active.id)} disabled={busy}
                    data-testid="breakglass-revoke-button">
              Revoke now
            </Button>
          </div>
          <div className="text-xs text-[var(--fop-text-3)]">Reason: {active.reason}</div>
          {active.approved_by && <div className="text-xs text-[var(--fop-text-3)]">Approval: {active.approved_by}</div>}
          <Button size="sm" onClick={() => viewOverview(active.id)} data-testid="breakglass-view-content-button">
            View tenant content overview
          </Button>
          {overview && (
            <div className="mt-2 border border-[var(--fop-border)] rounded p-2 text-xs" data-testid="breakglass-content-overview">
              {overview.projects.length === 0 ? (
                <div className="text-[var(--fop-text-3)]">No projects.</div>
              ) : overview.projects.map((p) => (
                <div key={p.id} className="flex justify-between py-1 border-b border-[var(--fop-border)] last:border-b-0">
                  <span>{p.name}</span>
                  <span className="text-[var(--fop-text-3)]">
                    {p.document_count} docs · {p.photo_count} photos · {p.pin_count} pins
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="border border-[var(--fop-border)] rounded p-3 space-y-2">
          <textarea
            className="w-full text-xs bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded p-2"
            rows={2}
            placeholder="Required reason (min 10 characters) — e.g. Customer ticket #4471, investigating stuck document"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="breakglass-reason-input"
          />
          <Input
            placeholder="Approval reference (optional) — e.g. customer confirmed via support call"
            value={approvedBy}
            onChange={(e) => setApprovedBy(e.target.value)}
            data-testid="breakglass-approval-input"
          />
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[var(--fop-text-3)]">Duration (minutes, max 240)</Label>
            <Input
              type="number" min={1} max={240} value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-24" data-testid="breakglass-duration-input"
            />
          </div>
          <Button size="sm" onClick={openGrant} disabled={busy || reason.trim().length < 10}
                  data-testid="breakglass-open-button">
            {busy ? "Opening…" : "Open break-glass grant"}
          </Button>
        </div>
      )}

      <div className="border border-[var(--fop-border)] rounded">
        <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
          <span>Status</span><span>Actor</span><span>Reason</span><span>Created</span>
        </div>
        {!grants || grants.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[var(--fop-text-3)]">No break-glass grants for this tenant.</div>
        ) : grants.map((g) => (
          <div key={g.id} className="grid grid-cols-4 gap-2 px-3 py-2 text-xs items-center border-b border-[var(--fop-border)] last:border-b-0"
               data-testid={`breakglass-row-${g.id}`}>
            <span className={g.status === "active" ? "text-amber-400" : "text-[var(--fop-text-3)]"}>{g.status}</span>
            <span className="truncate">{g.actor_email}</span>
            <span className="truncate text-[var(--fop-text-3)]" title={g.reason}>{g.reason}</span>
            <span className="text-[var(--fop-text-3)]">{new Date(g.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

